import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const AVISO = 'A NCM, as alíquotas e o tratamento administrativo são instrumentos meramente informativos, que não substituem a consulta oficial ao Simulador de Tratamento Administrativo do Portal Único Siscomex.';
const LINK_TA = 'https://portalunico.siscomex.gov.br/talpco/x/#/simular-ta-importacao';

function nivelDescricao(parte) {
  const m = parte.match(/^(-+)/);
  return { nivel: m ? m[1].length : 0, texto: parte.replace(/^-+\s*/, '').trim() };
}

function descricaoCompacta(descricao) {
  return descricao.split(' | ').map(p => nivelDescricao(p).texto).join(' › ');
}

function HierarquiaDescricao({ descricao }) {
  const partes = descricao.split(' | ');
  return (
    <div>
      {partes.map((p, i) => {
        const { nivel, texto } = nivelDescricao(p);
        return (
          <div key={i} style={{
            marginLeft: nivel * 16,
            marginTop: i === 0 ? 0 : 3,
            fontSize: i === 0 ? 13 : 12,
            fontWeight: i === 0 ? 600 : 400,
            color: i === 0 ? '#0B0E1A' : '#3D4461',
          }}>
            {nivel > 0 && <span style={{ color: '#9095A8', marginRight: 5 }}>›</span>}
            {texto}
          </div>
        );
      })}
    </div>
  );
}

function fmtCofins(valor) {
  return (valor ?? 9.65).toString().replace('.', ',');
}

// Destaca o(s) termo(s) buscado(s) dentro de um texto, pra facilitar a leitura da lista
function destacarTermo(texto, termos) {
  const validos = (termos || []).filter(Boolean);
  if (!validos.length) return texto;
  const escapados = validos.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regexSplit = new RegExp('(' + escapados.join('|') + ')', 'gi');
  const regexTeste = new RegExp('^(' + escapados.join('|') + ')$', 'i');
  return texto.split(regexSplit).map((parte, i) =>
    regexTeste.test(parte)
      ? <mark key={i} style={{ background: '#ffe58a', padding: '0 1px', borderRadius: 2 }}>{parte}</mark>
      : parte
  );
}

export default function MentorNCM() {
  const { token } = useAuth();
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [ta, setTa] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [sugestoesIA, setSugestoesIA] = useState([]);
  const [loadingIA, setLoadingIA] = useState(false);

  const consultar = async () => {
    if (busca.trim().length < 3) return;
    setLoading(true); setErro(''); setResultados([]); setSelecionado(null); setTa([]); setSugestoesIA([]);
    try {
      const res = await fetch(`/ia/ncm/busca?q=${encodeURIComponent(busca.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro');
      setResultados(data);
    } catch(e) { setErro(e.message); }
    finally { setLoading(false); }
  };

  // Busca automática enquanto digita (debounce de 350ms) — o botão "Consultar
  // na TEC" continua funcionando também, pra quem preferir forçar a busca.
  useEffect(() => {
    if (selecionado) return; // não busca de novo enquanto vê um detalhe
    if (busca.trim().length < 3) { setResultados([]); setErro(''); return; }
    const t = setTimeout(() => { consultar(); }, 350);
    return () => clearTimeout(t);
  }, [busca]);

  const consultarIA = async () => {
    if (busca.trim().length < 3) return;
    setLoadingIA(true); setSugestoesIA([]);
    try {
      const res = await fetch(`/ia/ncm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ produto: busca.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao consultar a IA');
      setSugestoesIA(data.sugestoes || []);
    } catch(e) { setErro(e.message); }
    finally { setLoadingIA(false); }
  };

  const verDetalhe = async (r) => {
    const ncmFmt = r.ncm.replace(/(\d{4})(\d{2})(\d{2})/, '$1.$2.$3');
    setSelecionado({ ...r, ncmFmt });
    try {
      const res = await fetch(`/ia/ncm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ncm: ncmFmt })
      });
      const data = await res.json();
      setTa(data.tratamento_administrativo || []);
      setSelecionado(prev => ({ ...prev, ...data }));
    } catch(e) {}
  };

  const selecionarSugestaoIA = (s) => {
    setTa(s.tratamento_administrativo || []);
    setSelecionado({ ncm: (s.ncm || '').replace(/\./g, ''), ncmFmt: s.ncm, descricao: s.descricao, ii: s.ii, ipi: s.ipi, pis: s.pis, cofins: s.cofins });
    setSugestoesIA([]);
  };

  return (
    <div style={{padding:'28px 32px',maxWidth:900}}>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:22,fontWeight:700,letterSpacing:'-.02em'}}>🔍 Consulta NCM</div>
        <div style={{fontSize:13,color:'#6B7289',marginTop:3}}>Busque por produto ou código NCM — alíquotas e tratamento administrativo</div>
      </div>

      <div className="card" style={{marginBottom:20}}>
        <div style={{display:'flex',gap:10}}>
          <input className="input-field" style={{flex:1}} placeholder="Ex: fone bluetooth, 8518.30.00, cabo USB..."
            value={busca} onChange={e=>setBusca(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&consultar()} />
          <button className="btn-primary" onClick={consultar} disabled={loading||busca.trim().length<3} style={{padding:'10px 24px'}}>
            {loading ? 'Buscando...' : 'Consultar na TEC'}
          </button>
        </div>
        {erro && <div style={{background:'#FDEAEA',borderRadius:8,padding:'9px 14px',fontSize:12,color:'#922020',marginTop:12}}>{erro}</div>}
      </div>

      {resultados.length > 0 && !selecionado && (
        <div className="card" style={{marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>{resultados.length} resultado{resultados.length>1?'s':''} encontrado{resultados.length>1?'s':''}:</div>
          {resultados.map((r,i) => {
            const ncmFmt = r.ncm.replace(/(\d{4})(\d{2})(\d{2})/, '$1.$2.$3');
            return (
              <div key={i} onClick={()=>verDetalhe(r)}
                style={{display:'flex',alignItems:'center',gap:14,padding:'12px 14px',borderRadius:8,border:'1px solid #E4E6EF',marginBottom:8,cursor:'pointer',transition:'.15s'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor='#1A2ECC'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='#E4E6EF'}>
                <div style={{background:'#EBF0FF',color:'#1028A0',fontWeight:700,fontSize:13,padding:'6px 12px',borderRadius:6,flexShrink:0,fontFamily:'monospace'}}>{ncmFmt}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,color:'#0B0E1A',lineHeight:1.4}}>{destacarTermo(descricaoCompacta(r.descricao), busca.trim().split(/\s+/))}</div>
                  <div style={{fontSize:11,color:'#6B7289',marginTop:2}}>
                    II: {r.ii_aliquota !== null ? r.ii_aliquota+'%' : '—'} · IPI: {r.ipi||0}% · PIS: 2,1% · COFINS: {fmtCofins(r.cofins)}%
                    {parseInt(r.tem_ta) > 0 && <span style={{color:'#E6A817',marginLeft:8,fontWeight:600}}>⚠️ TA: {r.orgaos_ta}</span>}
                  </div>
                </div>
                <span style={{color:'#1A2ECC',fontSize:13,fontWeight:600,flexShrink:0}}>Ver detalhes →</span>
              </div>
            );
          })}
        </div>
      )}

      {!selecionado && busca.trim().length >= 3 && !loading && (resultados.length > 0 || erro === '') && (
        <div style={{marginBottom:20}}>
          <button onClick={consultarIA} disabled={loadingIA}
            style={{fontSize:12,fontWeight:600,padding:'9px 16px',background:'#EAF2FA',color:'#1A5F9E',border:'none',borderRadius:8,cursor:'pointer'}}>
            {loadingIA ? 'Consultando IA...' : '🤖 Não encontrou? Pedir sugestão à IA'}
          </button>
          {sugestoesIA.length > 0 && (
            <div className="card" style={{marginTop:10}}>
              {sugestoesIA.map((s,i)=>(
                <div key={i} onClick={()=>selecionarSugestaoIA(s)}
                  style={{display:'flex',alignItems:'center',gap:14,padding:'12px 14px',borderRadius:8,border:'1px solid #E4E6EF',marginBottom:8,cursor:'pointer'}}>
                  <div style={{background:'#FFF7E0',color:'#8C5E00',fontWeight:700,fontSize:13,padding:'6px 12px',borderRadius:6,flexShrink:0,fontFamily:'monospace'}}>🤖 {s.ncm}</div>
                  <div style={{flex:1,fontSize:12,color:'#0B0E1A'}}>{s.descricao}</div>
                </div>
              ))}
              <div style={{fontSize:11,color:'#8C5E00',marginTop:4}}>⚠️ Sugestão de IA — confira o NCM antes de usar em operações reais.</div>
            </div>
          )}
        </div>
      )}

      {selecionado && (
        <div className="card" style={{borderLeft:'3px solid #1A2ECC'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:600}}>📋 Detalhes da NCM</div>
            <button onClick={()=>{setSelecionado(null);setTa([]);}} style={{background:'none',border:'none',color:'#6B7289',cursor:'pointer',fontSize:13}}>← Voltar</button>
          </div>

          <div style={{background:'#EBF0FF',borderRadius:10,padding:'12px 16px',marginBottom:14}}>
            <div style={{fontSize:11,color:'#6B7289',marginBottom:4}}>NCM</div>
            <div style={{fontSize:22,fontWeight:700,color:'#1028A0',fontFamily:'monospace'}}>{selecionado.ncmFmt || selecionado.ncm}</div>
            {selecionado.descricao && (
              <div style={{marginTop:6}}>
                <HierarquiaDescricao descricao={selecionado.descricao} />
              </div>
            )}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
            {[
              ['II', selecionado.ii ?? selecionado.ii_aliquota, '#1A2ECC'],
              ['IPI', selecionado.ipi || 0, '#8B5CF6'],
              ['PIS', selecionado.pis ?? 2.1, '#0FA36B'],
              ['COFINS', selecionado.cofins ?? 9.65, '#E6A817'],
            ].map(([nome, valor, cor])=>(
              <div key={nome} style={{background:'#F7F8FC',borderRadius:10,padding:'12px',textAlign:'center'}}>
                <div style={{fontSize:11,color:'#6B7289',marginBottom:4}}>{nome}</div>
                <div style={{fontSize:20,fontWeight:700,color:cor}}>{valor !== null && valor !== undefined ? valor : '—'}%</div>
              </div>
            ))}
          </div>

          {selecionado.cofins > 9.65 && (
            <div style={{background:'#FFF7E0',border:'1px solid #F0D580',borderRadius:8,padding:'9px 14px',fontSize:11,color:'#8C5E00',marginBottom:14,lineHeight:1.5}}>
              ℹ️ Cofins inclui o adicional de 0,6% previsto no Art. 8º, §21-A da Lei 10.865/2004 (vigente até 31/12/2026) para este capítulo da NCM.
            </div>
          )}

          {ta.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:'#E6A817',marginBottom:10}}>⚠️ Tratamento Administrativo</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'#FEF5E4'}}>
                    {['Órgão Anuente','Tipo de TA','Descrição'].map(h=>(
                      <th key={h} style={{padding:'8px 12px',textAlign:'left',borderBottom:'1px solid #F0D580',color:'#8C5E00'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ta.map((t,i)=>(
                    <tr key={i} style={{borderBottom:'0.5px solid #F4F5F9'}}>
                      <td style={{padding:'8px 12px',fontWeight:600}}>{t.orgao}</td>
                      <td style={{padding:'8px 12px',color:'#6B7289'}}>{t.tipo_ta}</td>
                      <td style={{padding:'8px 12px',color:'#6B7289'}}>{t.descricao||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{background:'#FDEAEA',borderRadius:8,padding:'10px 14px',fontSize:11,color:'#922020',lineHeight:1.6}}>
            ⚠️ {AVISO}{' '}
            <a href={LINK_TA} target="_blank" rel="noreferrer" style={{color:'#922020',fontWeight:600}}>Acessar Simulador →</a>
          </div>
        </div>
      )}
    </div>
  );
}
